import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, ApiError } from '../lib/api-client.js';
import { TtsVoiceSelect } from '../components/TtsVoiceSelect.js';
import { TIMEZONES } from '@xsc/shared';

/**
 * Customer Config admin page.
 *
 * Edits the per-tenant `customer_config` row that powers dialplan-var
 * injection on every voice command (CID, Wildix REST creds, country/prefix
 * routing, branding). Without a row, voice reminders fail fast with
 * `customer_config_missing`.
 *
 * Wildix REST password handling: rendered as `<input type="password">`,
 * never echoed back in plaintext from the server (server returns a masked
 * value when one is set). The form sends the field only when the operator
 * has typed a new value — leaving it blank preserves the stored credential.
 */

interface CustomerConfig {
  customer_id: string;
  country_code: string;
  prefix_nat: string;
  prefix_int: string;
  international_default: boolean;
  cid_name: string;
  cid_number: string;
  /** Server-masked: the literal string '***' when set, '' when unset. */
  wildix_rest_userpwd: string;
  tts_voice_default: string;
  intro_message_default: string | null;
  closing_message_default: string | null;
  eventfile_dir: string;
  co_name: string | null;
  co_address: string | null;
  co_city: string | null;
  co_state: string | null;
  co_postal: string | null;
  co_logo: string | null;
  co_email: string | null;
  co_tel: string | null;
  co_fax: string | null;
  co_from: string | null;
  co_timezone: string | null;
  co_web: string | null;
  co_privacy: string | null;
  co_terms: string | null;
  allow_user_email_connect: boolean;
  created_at: string;
  updated_at: string;
}

type FormState = Omit<CustomerConfig, 'customer_id' | 'created_at' | 'updated_at' | 'wildix_rest_userpwd'> & {
  /** Empty string = "leave existing credential alone". */
  wildix_rest_userpwd_new: string;
};

const COUNTRY_CODE_RE = /^[0-9]{1,4}$/;
const PREFIX_RE = /^[0-9]{0,5}$/;

export function CustomerConfigPage(): JSX.Element {
  const qc = useQueryClient();

  // Look up the operator's customer first — admin scope; the dev token is
  // bound to a single tenant in Phase 2 so we don't need a tenant picker.
  const { data: customer } = useQuery({
    queryKey: ['customer'],
    queryFn: () => api.get('/v1/customer') as Promise<{ id: string; name: string }>,
  });

  const customerId = customer?.id;

  const { data, isLoading, error, isError } = useQuery({
    queryKey: ['customer-config', customerId],
    enabled: !!customerId,
    queryFn: () =>
      api.get(`/v1/admin/customer-config/${customerId}`) as Promise<CustomerConfig>,
    retry: (n, err) => (err instanceof ApiError && [401, 403, 404].includes(err.status) ? false : n < 2),
  });

  const [form, setForm] = useState<FormState | null>(null);
  const [savedAt, setSavedAt] = useState<number | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);

  useEffect(() => {
    if (data) {
      setForm({
        country_code: data.country_code,
        prefix_nat: data.prefix_nat,
        prefix_int: data.prefix_int,
        international_default: data.international_default,
        cid_name: data.cid_name,
        cid_number: data.cid_number,
        wildix_rest_userpwd_new: '',
        tts_voice_default: data.tts_voice_default,
        intro_message_default: data.intro_message_default ?? '',
        closing_message_default: data.closing_message_default ?? '',
        eventfile_dir: data.eventfile_dir,
        co_name: data.co_name ?? '',
        co_address: data.co_address ?? '',
        co_city: data.co_city ?? '',
        co_state: data.co_state ?? '',
        co_postal: data.co_postal ?? '',
        co_logo: data.co_logo ?? '',
        co_email: data.co_email ?? '',
        co_tel: data.co_tel ?? '',
        co_fax: data.co_fax ?? '',
        co_from: data.co_from ?? '',
        co_timezone: data.co_timezone ?? '',
        co_web: data.co_web ?? '',
        co_privacy: data.co_privacy ?? '',
        co_terms: data.co_terms ?? '',
        allow_user_email_connect: data.allow_user_email_connect ?? true,
      });
    }
  }, [data]);

  const save = useMutation({
    mutationFn: (patch: Partial<FormState>) =>
      api.put(`/v1/admin/customer-config/${customerId}`, patch) as Promise<CustomerConfig>,
    onSuccess: () => {
      setSavedAt(Date.now());
      setSaveError(null);
      qc.invalidateQueries({ queryKey: ['customer-config', customerId] });
      // Clear the typed-in credential after a successful save so it doesn't
      // get re-sent on the next save (which would be wasted re-encrypts).
      setForm((f) => (f ? { ...f, wildix_rest_userpwd_new: '' } : f));
    },
    onError: (err) => {
      setSaveError(err instanceof Error ? err.message : 'Save failed');
    },
  });

  if (!customerId || isLoading) return <p className="text-sm text-ink-muted">Loading…</p>;
  if (isError) {
    const status = error instanceof ApiError ? error.status : 0;
    if (status === 404) {
      return (
        <div className="rounded border border-warning bg-surface-alt p-4">
          <p className="font-medium">No customer config yet</p>
          <p className="text-sm text-ink-muted mt-1">
            This tenant has no <code>customer_config</code> row. Run the seed
            migration or have an operator POST one before voice reminders will
            dispatch.
          </p>
        </div>
      );
    }
    return (
      <div className="rounded border border-danger bg-surface-alt p-4">
        <p className="font-medium text-danger">Failed to load customer config</p>
        <p className="text-sm text-ink-muted mt-1">{(error as Error).message}</p>
      </div>
    );
  }
  if (!form || !data) return <p>No data.</p>;

  const onSubmit = (e: React.FormEvent): void => {
    e.preventDefault();

    // Validation: country code numeric ≤4, prefixes numeric ≤5, eventfile_dir absolute,
    // cid_number non-empty, tts_voice_default non-empty.
    if (!COUNTRY_CODE_RE.test(form.country_code)) {
      setSaveError('Country code must be 1–4 digits');
      return;
    }
    if (!PREFIX_RE.test(form.prefix_nat) || !PREFIX_RE.test(form.prefix_int)) {
      setSaveError('Prefixes must be 0–5 digits');
      return;
    }
    if (!form.eventfile_dir.startsWith('/')) {
      setSaveError('Eventfile directory must be an absolute path');
      return;
    }
    if (!form.cid_number.trim() || !form.tts_voice_default.trim()) {
      setSaveError('Caller-ID number and TTS voice are required');
      return;
    }

    // Send only changed-from-original keys + the password only when typed.
    const patch: Record<string, unknown> = {};
    const pairs: Array<[keyof FormState, unknown, unknown]> = [
      ['country_code', form.country_code, data.country_code],
      ['prefix_nat', form.prefix_nat, data.prefix_nat],
      ['prefix_int', form.prefix_int, data.prefix_int],
      ['international_default', form.international_default, data.international_default],
      ['cid_name', form.cid_name, data.cid_name],
      ['cid_number', form.cid_number, data.cid_number],
      ['tts_voice_default', form.tts_voice_default, data.tts_voice_default],
      ['intro_message_default', form.intro_message_default, data.intro_message_default ?? ''],
      ['closing_message_default', form.closing_message_default, data.closing_message_default ?? ''],
      ['eventfile_dir', form.eventfile_dir, data.eventfile_dir],
      ['co_name', form.co_name, data.co_name ?? ''],
      ['co_address', form.co_address, data.co_address ?? ''],
      ['co_city', form.co_city, data.co_city ?? ''],
      ['co_state', form.co_state, data.co_state ?? ''],
      ['co_postal', form.co_postal, data.co_postal ?? ''],
      ['co_logo', form.co_logo, data.co_logo ?? ''],
      ['co_email', form.co_email, data.co_email ?? ''],
      ['co_tel', form.co_tel, data.co_tel ?? ''],
      ['co_fax', form.co_fax, data.co_fax ?? ''],
      ['co_from', form.co_from, data.co_from ?? ''],
      ['co_timezone', form.co_timezone, data.co_timezone ?? ''],
      ['co_web', form.co_web, data.co_web ?? ''],
      ['co_privacy', form.co_privacy, data.co_privacy ?? ''],
      ['co_terms', form.co_terms, data.co_terms ?? ''],
      ['allow_user_email_connect', form.allow_user_email_connect, data.allow_user_email_connect ?? true],
    ];
    for (const [key, current, original] of pairs) {
      if (current !== original) patch[key] = current === '' ? null : current;
    }
    if (form.wildix_rest_userpwd_new.trim()) {
      patch['wildix_rest_userpwd'] = form.wildix_rest_userpwd_new;
    }
    if (Object.keys(patch).length === 0) {
      setSaveError('No changes to save');
      return;
    }
    save.mutate(patch);
  };

  return (
    <form onSubmit={onSubmit} className="space-y-6 max-w-3xl">
      <header className="flex items-baseline justify-between">
        <div>
          <h1 className="text-2xl font-semibold">Customer Config</h1>
          <p className="text-sm text-ink-muted font-mono">{data.customer_id}</p>
        </div>
        {savedAt && Date.now() - savedAt < 4000 && (
          <span className="text-sm text-success">Saved ✓</span>
        )}
      </header>

      {saveError && (
        <div className="rounded border border-danger bg-surface-alt p-3 text-sm text-danger">
          {saveError}
        </div>
      )}

      <Section title="Phone routing">
        <Field label="Country code (digits)" hint="e.g. 1 for US, 44 for UK">
          <input
            className={inputCls}
            value={form.country_code}
            onChange={(e) => setForm({ ...form, country_code: e.target.value })}
          />
        </Field>
        <Field label="National dial prefix" hint="prepended for in-country calls">
          <input
            className={inputCls}
            value={form.prefix_nat}
            onChange={(e) => setForm({ ...form, prefix_nat: e.target.value })}
          />
        </Field>
        <Field label="International dial prefix" hint="prepended for cross-border calls">
          <input
            className={inputCls}
            value={form.prefix_int}
            onChange={(e) => setForm({ ...form, prefix_int: e.target.value })}
          />
        </Field>
        <Field label="Default to international">
          <input
            type="checkbox"
            checked={form.international_default}
            onChange={(e) => setForm({ ...form, international_default: e.target.checked })}
          />
        </Field>
      </Section>

      <Section title="Caller ID">
        <Field label="Caller-ID name">
          <input
            className={inputCls}
            value={form.cid_name}
            onChange={(e) => setForm({ ...form, cid_name: e.target.value })}
          />
        </Field>
        <Field label="Caller-ID number" hint="E.164 (e.g. +12025550100)">
          <input
            className={inputCls}
            value={form.cid_number}
            onChange={(e) => setForm({ ...form, cid_number: e.target.value })}
          />
        </Field>
      </Section>

      <Section title="Wildix REST">
        <Field
          label="Wildix REST credential"
          hint={
            data.wildix_rest_userpwd
              ? 'A credential is set (masked). Type to replace; leave blank to keep.'
              : 'Format: <ext>:<password>, e.g. 999:supersecret'
          }
        >
          <input
            type="password"
            autoComplete="new-password"
            placeholder={data.wildix_rest_userpwd ? '••••••••' : ''}
            className={inputCls}
            value={form.wildix_rest_userpwd_new}
            onChange={(e) =>
              setForm({ ...form, wildix_rest_userpwd_new: e.target.value })
            }
          />
        </Field>
      </Section>

      <Section title="Audio">
        <Field label="Default TTS voice" hint="Used when an event or template does not specify a voice.">
          <TtsVoiceSelect
            className={inputCls}
            value={form.tts_voice_default}
            onChange={(v) => setForm({ ...form, tts_voice_default: v })}
          />
        </Field>
        <Field label="Default intro message">
          <textarea
            className={inputCls}
            rows={2}
            value={form.intro_message_default ?? ''}
            onChange={(e) => setForm({ ...form, intro_message_default: e.target.value })}
          />
        </Field>
        <Field label="Default closing message">
          <textarea
            className={inputCls}
            rows={2}
            value={form.closing_message_default ?? ''}
            onChange={(e) => setForm({ ...form, closing_message_default: e.target.value })}
          />
        </Field>
      </Section>

      <Section title="Company">
        <Field label="Company name">
          <TextInput v={form.co_name} on={(v) => setForm({ ...form, co_name: v })} />
        </Field>
        <Field label="Address">
          <TextInput v={form.co_address} on={(v) => setForm({ ...form, co_address: v })} />
        </Field>
        <Field label="City">
          <TextInput v={form.co_city} on={(v) => setForm({ ...form, co_city: v })} />
        </Field>
        <Field label="State">
          <TextInput v={form.co_state} on={(v) => setForm({ ...form, co_state: v })} />
        </Field>
        <Field label="Postal code">
          <TextInput v={form.co_postal} on={(v) => setForm({ ...form, co_postal: v })} />
        </Field>
        <Field label="Logo URL">
          <TextInput v={form.co_logo} on={(v) => setForm({ ...form, co_logo: v })} />
        </Field>
        <Field label="Email">
          <TextInput v={form.co_email} on={(v) => setForm({ ...form, co_email: v })} />
        </Field>
        <Field label="Phone">
          <TextInput v={form.co_tel} on={(v) => setForm({ ...form, co_tel: v })} />
        </Field>
        <Field label="Fax">
          <TextInput v={form.co_fax} on={(v) => setForm({ ...form, co_fax: v })} />
        </Field>
        <Field label="From (display name on outbound emails)">
          <TextInput v={form.co_from} on={(v) => setForm({ ...form, co_from: v })} />
        </Field>
        <Field label="Timezone" hint="Default display timezone for this tenant">
          <select
            className={inputCls}
            value={form.co_timezone ?? ''}
            onChange={(e) => setForm({ ...form, co_timezone: e.target.value })}
          >
            <option value="">— Not set —</option>
            {TIMEZONES.map((tz) => (
              <option key={tz.value} value={tz.value}>
                {tz.label}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Website URL">
          <TextInput v={form.co_web} on={(v) => setForm({ ...form, co_web: v })} />
        </Field>
        <Field label="Privacy policy URL">
          <TextInput v={form.co_privacy} on={(v) => setForm({ ...form, co_privacy: v })} />
        </Field>
        <Field label="Terms URL">
          <TextInput v={form.co_terms} on={(v) => setForm({ ...form, co_terms: v })} />
        </Field>
      </Section>

      <Section title="Policies">
        <Field
          label="Allow non-admin users to connect their own email account"
          hint="When off, only admins can connect Gmail accounts via OAuth."
        >
          <input
            type="checkbox"
            checked={form.allow_user_email_connect}
            onChange={(e) =>
              setForm({ ...form, allow_user_email_connect: e.target.checked })
            }
          />
        </Field>
      </Section>

      <Section title="Advanced">
        <Field label="Eventfile directory" hint="Absolute path on the PBX (must be writable by the connector user)">
          <input
            className={inputCls}
            value={form.eventfile_dir}
            onChange={(e) => setForm({ ...form, eventfile_dir: e.target.value })}
          />
        </Field>
      </Section>

      <div className="flex items-center gap-3">
        <button
          type="submit"
          disabled={save.isPending}
          className="rounded bg-accent px-4 py-2 text-sm font-medium text-ink-on-accent disabled:opacity-50"
        >
          {save.isPending ? 'Saving…' : 'Save changes'}
        </button>
        <p className="text-xs text-ink-muted">
          Updated {new Date(data.updated_at).toLocaleString()}
        </p>
      </div>
    </form>
  );
}

const inputCls =
  'w-full rounded border border-border bg-surface px-2 py-1 text-sm font-mono';

function Section({ title, children }: { title: string; children: React.ReactNode }): JSX.Element {
  return (
    <section className="rounded border border-border bg-surface-alt p-4 space-y-3">
      <h2 className="font-medium">{title}</h2>
      {children}
    </section>
  );
}

function Field({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: string;
  children: React.ReactNode;
}): JSX.Element {
  return (
    <label className="block">
      <div className="text-sm font-medium">{label}</div>
      {hint && <div className="text-xs text-ink-muted mb-1">{hint}</div>}
      {children}
    </label>
  );
}

function TextInput({
  v,
  on,
}: {
  v: string | null;
  on: (s: string) => void;
}): JSX.Element {
  return (
    <input className={inputCls} value={v ?? ''} onChange={(e) => on(e.target.value)} />
  );
}
